import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../config/axios';
import { useLanguage } from '../../context/LanguageContext';
import { MdAdd, MdEdit, MdDelete, MdClose, MdArrowBack, MdChevronRight, MdAssignment } from 'react-icons/md';
import { CiMenuKebab } from "react-icons/ci";

const SEVERITY_TYPES = [
  { value: 'S1: Production Server Down', label: 'S1: Production Server Down' },
  { value: 'S2: Major Functionality Severely impaired', label: 'S2: Major Functionality Severely impaired' },
  { value: 'S3: Partial, non-critical loss of functionality', label: 'S3: Partial, non-critical loss of functionality' },
  { value: 'S4: General, Usage Issues', label: 'S4: General, Usage Issues' },
  { value: 'Other', label: 'Other' }
];

const SLA_TYPES = [
  { value: '5/8', label: '5/8' },
  { value: '7/24', label: '7/24' },
  { value: 'Other', label: 'Other' }
];

// Saat seçenekleri için array (00-23 arası)
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => {
  const hour = i.toString().padStart(2, '0');
  return { value: hour, label: hour };
});

// Hafta günleri için array
const WEEK_DAYS = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' }
];

const SlaDetail = () => {
  const { t } = useLanguage();
  const { id } = useParams(); // Bu artık UUID olacak
  const navigate = useNavigate();
  const [sla, setSla] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDefinitionForm, setShowDefinitionForm] = useState(false);
  const [formData, setFormData] = useState({
    sla_type: '',
    sla_severity: '',
    sla_response_time: '',
    sla_resoution_time: '',
    sla_work_hours_start: '',
    sla_work_hours_end: '',
    sla_work_week: [],
    custom_type: '',
    custom_severity: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showPopup, setShowPopup] = useState({ show: false, message: '', type: '' });

  // SLA edit için state'ler
  const [showSlaEditForm, setShowSlaEditForm] = useState(false);
  const [slaFormData, setSlaFormData] = useState({
    sla_group_name: '',
    description: ''
  });
  const [isSlaEditing, setIsSlaEditing] = useState(false);
  const [slaEditingId, setSlaEditingId] = useState(null);

  // Assign için state'ler
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerContracts, setCustomerContracts] = useState([]);
  const [contractProducts, setContractProducts] = useState([]);
  const [assignFormData, setAssignFormData] = useState({
    customer_id: '',
    contract_id: '',
    product_id: ''
  });
  const [showSlaActionsMenu, setShowSlaActionsMenu] = useState(false);
  const [showDefinitionsActionsMenu, setShowDefinitionsActionsMenu] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  // Menülerin dışına tıklandığında kapanması için
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.relative')) {
        setShowSlaActionsMenu(false);
        setShowDefinitionsActionsMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchData = async () => {
    try {
      const response = await api.get(`/sla/${id}`);
      if (response.data.success) {
        setSla(response.data.sla);
      }
    } catch (error) {
      console.error('Veri yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  // SLA edit fonksiyonu - Edit popup'ını aç
  const handleSlaEdit = () => {
    setSlaFormData({
      sla_group_name: sla.sla_group_name || '',
      description: sla.description || ''
    });
    setIsSlaEditing(true);
    setSlaEditingId(sla.id);
    setShowSlaEditForm(true);
  };

  // SLA delete fonksiyonu
  const handleSlaDelete = () => {
    setShowPopup({
      show: true,
      type: 'confirm',
      message: `"${sla.sla_group_name}" isimli SLA grubunu silmek istediğinizden emin misiniz?`,
      onConfirm: async () => {
        try {
          const response = await api.delete(`/sla/${sla.id}`);
          if (response.data.success) {
            setShowPopup({
              show: true,
              type: 'success',
              message: `"${sla.sla_group_name}" başarıyla silindi`,
              onConfirm: () => {
                setShowPopup({ show: false });
                navigate('/settings/sla');
              }
            });
          }
        } catch (error) {
          setShowPopup({
            show: true,
            type: 'error',
            message: error.response?.data?.message || 'SLA silinirken bir hata oluştu'
          });
        }
      },
      onCancel: () => setShowPopup({ show: false })
    });
  };



  // TIME formatından saat değerini çıkaran fonksiyon
  const extractHourFromTime = (timeString) => {
    if (!timeString) return '';
    // "09:00:00" formatından "09" değerini çıkar
    return timeString.split(':')[0];
  };

  // Work week checkbox'larını handle eden fonksiyon
  const handleWorkWeekChange = (dayValue) => {
    setFormData(prev => ({
      ...prev,
      sla_work_week: prev.sla_work_week.includes(dayValue)
        ? prev.sla_work_week.filter(day => day !== dayValue)
        : [...prev.sla_work_week, dayValue]
    }));
  };

  // String'den work week array'ine çeviren fonksiyon
  const parseWorkWeekString = (workWeekString) => {
    if (!workWeekString) return [];
    return workWeekString.split(',').map(day => day.trim());
  };

  const handleEdit = (definition) => {
    setFormData({
      sla_type: definition.sla_type,
      sla_severity: definition.sla_severity,
      sla_response_time: definition.sla_response_time,
      sla_resoution_time: definition.sla_resoution_time,
      sla_work_hours_start: extractHourFromTime(definition.sla_work_hours_start),
      sla_work_hours_end: extractHourFromTime(definition.sla_work_hours_end),
      sla_work_week: parseWorkWeekString(definition.sla_work_days)
    });
    setEditingId(definition.id);
    setIsEditing(true);
    setShowDefinitionForm(true);
  };

  const handleDelete = async (definitionId) => {
    setShowPopup({
      show: true,
      type: 'confirm',
      message: 'Are you sure you want to delete this SLA definition?',
      onConfirm: async () => {
        try {
          await api.delete(`/sla/definition/${definitionId}`);
          fetchData();
          setShowPopup({ show: false });
        } catch (error) {
          console.error('Error deleting SLA definition:', error);
          setShowPopup({
            show: true,
            type: 'error',
            message: 'An error occurred while deleting the SLA definition.'
          });
        }
      },
      onCancel: () => setShowPopup({ show: false })
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        sla_type: formData.sla_type === 'Other' ? formData.custom_type : formData.sla_type,
        sla_severity: formData.sla_severity === 'Other' ? formData.custom_severity : formData.sla_severity,
        sla_response_time: formData.sla_response_time,
        sla_resoution_time: formData.sla_resoution_time,
        sla_work_hours_start: formData.sla_work_hours_start ? `${formData.sla_work_hours_start}:00:00` : null,
        sla_work_hours_end: formData.sla_work_hours_end ? `${formData.sla_work_hours_end}:00:00` : null,
        sla_work_week: formData.sla_work_week.length > 0 ? formData.sla_work_week.join(',') : null,
        product_sla_id: sla?.id // UUID yerine gerçek SLA ID'sini kullan
      };



      if (isEditing) {
        await api.put(`/sla/definition/${editingId}`, submitData);
      } else {
        await api.post('/sla/definition', submitData);
      }
      
      setShowDefinitionForm(false);
      setIsEditing(false);
      setEditingId(null);
      setFormData({
        sla_type: '',
        sla_severity: '',
        sla_response_time: '',
        sla_resoution_time: '',
        sla_work_hours_start: '',
        sla_work_hours_end: '',
        sla_work_week: [],
        custom_type: '',
        custom_severity: ''
      });
      fetchData();
    } catch (error) {
      console.error('Error saving SLA definition:', error);
      setShowPopup({
        show: true,
        type: 'error',
        message: 'An error occurred while saving the SLA definition.'
      });
    }
  };

  // SLA edit form submit fonksiyonu
  const handleSlaSubmit = async (e) => {
    e.preventDefault();
    
    if (!slaFormData.sla_group_name.trim()) {
      setShowPopup({
        show: true,
        type: 'error',
        message: 'SLA grup adı boş olamaz'
      });
      return;
    }

    try {
      const userStr = localStorage.getItem('user');
      const userData = JSON.parse(userStr);
      
      const response = await api.put(`/sla/${slaEditingId}`, {
        ...slaFormData,
        company_id: userData.companyId
      });

      if (response.data.success) {
        setShowPopup({
          show: true,
          type: 'success',
          message: 'SLA başarıyla güncellendi',
          onConfirm: () => {
            setShowSlaEditForm(false);
            setIsSlaEditing(false);
            setSlaEditingId(null);
            fetchData(); // SLA verilerini yenile
            setShowPopup({ show: false });
          }
        });
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        navigate('/admin');
      } else {
        setShowPopup({
          show: true,
          type: 'error',
          message: error.response?.data?.message || 'SLA güncellenirken bir hata oluştu'
        });
      }
    }
  };

  // SLA form change handler
  const handleSlaChange = (e) => {
    const { name, value } = e.target;
    setSlaFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Müşterileri getir
  const fetchCustomers = async () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const userData = JSON.parse(userStr);
        const response = await api.get(`/customers/${userData.companyId}`);
        if (response.data.success) {
          setCustomers(response.data.customers);
        }
      }
    } catch (error) {
      console.error('Müşteriler yüklenirken hata:', error);
    }
  };

  // Müşteri seçildiğinde kontratları getir
  const handleCustomerChange = async (e) => {
    const customerId = e.target.value;
    setAssignFormData({
      customer_id: customerId,
      contract_id: '',
      product_id: ''
    });
    setContractProducts([]);
    
    if (customerId) {
      try {
        const response = await api.get(`/customer-contracts/${customerId}`);
        if (response.data.success) {
          setCustomerContracts(response.data.contracts);
        }
      } catch (error) {
        console.error('Kontratlar yüklenirken hata:', error);
      }
    } else {
      setCustomerContracts([]);
    }
  };

  // Kontrat seçildiğinde ürünleri getir
  const handleContractChange = async (e) => {
    const contractId = e.target.value;
    setAssignFormData(prev => ({
      ...prev,
      contract_id: contractId,
      product_id: ''
    }));
    
    if (contractId) {
      try {
        const response = await api.get(`/products/${contractId}`);
        if (response.data.success) {
          setContractProducts(response.data.products);
        }
      } catch (error) {
        console.error('Ürünler yüklenirken hata:', error);
      }
    } else {
      setContractProducts([]);
    }
  };

  // SLA atama işlemini başlat
  const handleAssignClick = () => {
    setAssignFormData({
      customer_id: '',
      contract_id: '',
      product_id: ''
    });
    setShowAssignForm(true);
    fetchCustomers();
  };

  // SLA atama işlemini tamamla
  const handleAssignSubmit = async () => {
    try {
      const response = await api.put(`/products/${assignFormData.product_id}/assign-sla`, {
        sla_id: sla.id
      });

      if (response.data.success) {
        // Önce form'u kapat ve state'i temizle
        setShowAssignForm(false);
        setAssignFormData({
          customer_id: '',
          contract_id: '',
          product_id: ''
        });
        
        // Sonra başarı popup'ını göster
        setShowPopup({
          show: true,
          type: 'success',
          message: 'SLA başarıyla atandı',
          onConfirm: () => {
            fetchData(); // SLA listesini güncelle
            setShowPopup({ show: false });
          }
        });
      }
    } catch (error) {
      setShowPopup({
        show: true,
        type: 'error',
        message: error.response?.data?.message || 'SLA atanırken bir hata oluştu'
      });
    }
  };

  if (loading) return <div>Loading...</div>;
  if (!sla) return <div>SLA bulunamadı</div>;

  return (
    <div >
      {/* Update navigation section */}
      <nav className="flex items-center text-sm mb-6 pt-4">
        <div className="flex items-center bg-[#232b3b] dark:bg-gray-700 px-4 py-2 rounded-md shadow-sm">
          <Link 
            to="/settings/sla" 
            className="text-[#fff] dark:text-white hover:text-[gray] dark:hover:text-gray-300 font-medium transition-colors duration-200"
          >
            {t('slaDetail.settings')}
          </Link>
          <MdChevronRight className="mx-2 text-[#fff] dark:text-white" size={20} />
          <Link 
            to="/settings/sla" 
            className="text-[#fff] dark:text-white hover:text-[gray] dark:hover:text-gray-300 font-medium transition-colors duration-200"
          >
            {t('slaDetail.slaGroups')}
          </Link>
          <MdChevronRight className="mx-2 text-[#fff] dark:text-white" size={20} />
          <span className="text-[#fff] dark:text-white font-medium">
            {sla.sla_group_name}
          </span>
        </div>
      </nav>

      {/* Main Content */}
      <div className="space-y-6">
        {/* SLA Details Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-t-lg flex justify-between items-center">
            <h1 className="text-lg font-medium text-gray-800 dark:text-white">{t('slaDetail.slaGroupDetails')}</h1>
            <div className="relative">
              <button
                onClick={() => setShowSlaActionsMenu(!showSlaActionsMenu)}
                className="text-gray-600 hover:text-gray-900 dark:hover:text-gray-300"
              >
                <CiMenuKebab size={20} />
              </button>
              
              {showSlaActionsMenu && (
                <div className="absolute right-0 top-10 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg z-50">
                  <div className="py-1">
                    <button
                      onClick={() => {
                        handleSlaEdit();
                        setShowSlaActionsMenu(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <MdEdit size={16} />
                      {t('slaDetail.edit')}
                    </button>
                    <button
                      onClick={() => {
                        handleSlaDelete();
                        setShowSlaActionsMenu(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <MdDelete size={16} />
                      {t('slaDetail.delete')}
                    </button>
                    <button
                      onClick={() => {
                        handleAssignClick();
                        setShowSlaActionsMenu(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <MdAssignment size={16} />
                      {t('slaDetail.assign')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="overflow-hidden">
            {/* Desktop Table View */}
            <div className="hidden lg:block">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                  <tr>
                    <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 ">{t('slaDetail.slaGroupName')}</td>
                    <td className="px-4 py-2 text-gray-900 dark:text-white">{sla.sla_group_name}</td>
                    <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('slaDetail.createdAt')}</td>
                    <td className="px-4 py-2 text-gray-900 dark:text-white">{new Date(sla.created_at).toLocaleDateString()}</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 rounded-bl-lg">{t('slaDetail.description')}</td>
                    <td className="px-4 py-2 text-gray-900 dark:text-white">{sla.description}</td>
                    <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('slaDetail.updatedAt')}</td>
                    <td className="px-4 py-2 text-gray-900 dark:text-white">
                    {new Date(sla.updated_at).toLocaleDateString()}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-4 p-4">
              {/* SLA Group Name & Created At */}
              <div className="grid grid-cols-1 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('slaDetail.slaGroupName')}</div>
                  <div className="text-gray-900 dark:text-white">{sla.sla_group_name}</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('slaDetail.createdAt')}</div>
                  <div className="text-gray-900 dark:text-white">{new Date(sla.created_at).toLocaleDateString()}</div>
                </div>
              </div>

              {/* Description & Updated At */}
              <div className="grid grid-cols-1 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('slaDetail.description')}</div>
                  <div className="text-gray-900 dark:text-white">{sla.description}</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('slaDetail.updatedAt')}</div>
                  <div className="text-gray-900 dark:text-white">{new Date(sla.updated_at).toLocaleDateString()}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

                 {/* SLA Definitions Card */}
         <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
           <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-t-lg flex justify-between items-center">
             <h2 className="text-lg font-medium text-gray-800 dark:text-white">{t('slaDetail.slaDefinitions')}</h2>
             <div className="relative">
               <button
                 onClick={() => setShowDefinitionsActionsMenu('add')}
                 className="text-gray-600 hover:text-gray-900 dark:hover:text-gray-300"
               >
                 <CiMenuKebab size={20} />
               </button>
               
               {showDefinitionsActionsMenu === 'add' && (
                 <div className="absolute right-0 top-10 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg z-[9999]">
                   <div className="py-1">
                     <button
                       onClick={() => {
                         setIsEditing(false);
                         setShowDefinitionForm(true);
                         setShowDefinitionsActionsMenu(null);
                       }}
                       className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                     >
                       <MdAdd size={16} />
                       {t('slaDetail.addDefinition')}
                     </button>
                   </div>
                 </div>
               )}
             </div>
           </div>
           

          <div className="overflow-visible">
            {/* Desktop Table View */}
            <div className="hidden lg:block">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('slaDetail.type')}</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('slaDetail.severity')}</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('slaDetail.responseTime')}</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('slaDetail.resolutionTime')}</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('slaDetail.workHours')}</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('slaDetail.workDays')}</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('slaDetail.actions')}</th>
                  </tr>
                </thead>
                                                           <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                  {!sla.definitions || sla.definitions.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-4 py-2 text-center text-sm text-gray-500 dark:text-gray-400 rounded-bl-lg rounded-br-lg">
                        {t('slaDetail.noDefinitions')}
                      </td>
                    </tr>
                  ) : (
                    sla.definitions.map((def, index) => {
                      const isLast = index === sla.definitions.length - 1;
                      return (
                        <tr key={def.id}>
                          <td className={`px-4 py-2 text-sm text-gray-900 dark:text-white ${isLast ? 'rounded-bl-lg' : ''}`}>{def.sla_type}</td>
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{def.sla_severity}</td>
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{def.sla_response_time} minutes</td>
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{def.sla_resoution_time} minutes</td>
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                            {def.sla_work_hours_start && def.sla_work_hours_end 
                              ? `${extractHourFromTime(def.sla_work_hours_start)}:00 - ${extractHourFromTime(def.sla_work_hours_end)}:00`
                              : t('slaDetail.notSpecified')
                            }
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                            {def.sla_work_days 
                              ? def.sla_work_days.split(',').map(day => day.trim()).join(', ')
                              : t('slaDetail.notSpecified')
                            }
                          </td>
                          <td className={`px-4 py-2 text-right text-sm font-medium ${isLast ? 'rounded-br-lg' : ''}`}>
                            <div className="relative inline-block">
                              <button
                                onClick={() => setShowDefinitionsActionsMenu(showDefinitionsActionsMenu === def.id ? null : def.id)}
                                className="text-gray-600 hover:text-gray-900 dark:hover:text-gray-300"
                              >
                                <CiMenuKebab size={18} />
                              </button>
                              
                              {showDefinitionsActionsMenu === def.id && (
                                <div className="absolute right-0 top-10 w-32 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg z-[9999]">
                                  <div className="py-1">
                                    <button
                                      onClick={() => {
                                        handleEdit(def);
                                        setShowDefinitionsActionsMenu(null);
                                      }}
                                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                    >
                                      <MdEdit size={16} />
                                      {t('slaDetail.edit')}
                                    </button>
                                    <button
                                      onClick={() => {
                                        handleDelete(def.id);
                                        setShowDefinitionsActionsMenu(null);
                                      }}
                                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                    >
                                      <MdDelete size={16} />
                                      {t('slaDetail.delete')}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-4 p-4">
              {!sla.definitions || sla.definitions.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-400 dark:text-gray-500 text-sm">{t('slaDetail.noDefinitions')}</p>
                </div>
              ) : (
                sla.definitions.map((def, index) => (
                  <div key={def.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-3">
                    {/* Type & Severity */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('slaDetail.type')}</div>
                        <div className="text-sm text-gray-900 dark:text-white font-medium">{def.sla_type}</div>
                      </div>
                      <div>
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('slaDetail.severity')}</div>
                        <div className="text-sm text-gray-900 dark:text-white">{def.sla_severity}</div>
                      </div>
                    </div>

                    {/* Response Time & Resolution Time */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('slaDetail.responseTime')}</div>
                        <div className="text-sm text-gray-900 dark:text-white">{def.sla_response_time} minutes</div>
                      </div>
                      <div>
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('slaDetail.resolutionTime')}</div>
                        <div className="text-sm text-gray-900 dark:text-white">{def.sla_resoution_time} minutes</div>
                      </div>
                    </div>

                    {/* Work Hours & Work Days */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('slaDetail.workHours')}</div>
                        <div className="text-sm text-gray-900 dark:text-white">
                          {def.sla_work_hours_start && def.sla_work_hours_end 
                            ? `${extractHourFromTime(def.sla_work_hours_start)}:00 - ${extractHourFromTime(def.sla_work_hours_end)}:00`
                            : t('slaDetail.notSpecified')
                          }
                        </div>
                      </div>
                      <div>
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('slaDetail.workDays')}</div>
                        <div className="text-sm text-gray-900 dark:text-white">
                          {def.sla_work_days 
                            ? def.sla_work_days.split(',').map(day => day.trim()).join(', ')
                            : t('slaDetail.notSpecified')
                          }
                        </div>
                      </div>
                    </div>

                    {/* Work Days & Actions */}
                    <div className="grid grid-cols-2 gap-4">
                      <div></div>
                      <div>
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('slaDetail.actions')}</div>
                        <div className="flex items-center gap-2">
                          <div className="relative inline-block">
                            <button
                              onClick={() => setShowDefinitionsActionsMenu(showDefinitionsActionsMenu === def.id ? null : def.id)}
                              className="text-gray-600 hover:text-gray-900 dark:hover:text-gray-300"
                            >
                              <CiMenuKebab size={18} />
                            </button>
                            
                            {showDefinitionsActionsMenu === def.id && (
                              <div className="absolute right-0 top-10 w-32 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg z-[9999]">
                                <div className="py-1">
                                  <button
                                    onClick={() => {
                                      handleEdit(def);
                                      setShowDefinitionsActionsMenu(null);
                                    }}
                                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                  >
                                    <MdEdit size={16} />
                                    {t('slaDetail.edit')}
                                  </button>
                                  <button
                                    onClick={() => {
                                      handleDelete(def.id);
                                      setShowDefinitionsActionsMenu(null);
                                    }}
                                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                  >
                                    <MdDelete size={16} />
                                    {t('slaDetail.delete')}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          
        </div>

        {/* Assigned Products Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-t-lg">
            <h2 className="text-lg font-medium text-gray-800 dark:text-white">{t('slaDetail.assignedProducts')}</h2>
          </div>
          <div className="overflow-hidden">
            {/* Desktop Table View */}
            <div className="hidden lg:block">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('slaDetail.customer')}</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('slaDetail.contract')}</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('slaDetail.product')}</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('slaDetail.assignedDate')}</th>
                  </tr>
                </thead>
                                 <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                   {!sla.assigned_products || sla.assigned_products.length === 0 ? (
                     <tr>
                       <td colSpan="4" className="px-4 py-2 text-center text-sm text-gray-500 dark:text-gray-400 rounded-bl-lg rounded-br-lg">
                         {t('slaDetail.noAssignedProducts')}
                       </td>
                     </tr>
                   ) : (
                     sla.assigned_products.map((product, index) => {
                       const isLast = index === sla.assigned_products.length - 1;
                       return (
                         <tr key={product.id}>
                           <td className={`px-4 py-2 text-sm text-gray-900 dark:text-white ${isLast ? 'rounded-bl-lg' : ''}`}>{product.customer_name}</td>
                           <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{product.contract_name}</td>
                           <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{product.product_name}</td>
                           <td className={`px-4 py-2 text-sm text-gray-900 dark:text-white ${isLast ? 'rounded-br-lg' : ''}`}>
                             {new Date(product.updated_at).toLocaleDateString()}
                           </td>
                         </tr>
                       );
                     })
                   )}
                 </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-4 p-4">
              {!sla.assigned_products || sla.assigned_products.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-400 dark:text-gray-500 text-sm">{t('slaDetail.noAssignedProducts')}</p>
                </div>
              ) : (
                sla.assigned_products.map((product, index) => (
                  <div key={product.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-3">
                    {/* Customer & Contract */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('slaDetail.customer')}</div>
                        <div className="text-sm text-gray-900 dark:text-white font-medium">{product.customer_name}</div>
                      </div>
                      <div>
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('slaDetail.contract')}</div>
                        <div className="text-sm text-gray-900 dark:text-white">{product.contract_name}</div>
                      </div>
                    </div>

                    {/* Product & Assigned Date */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('slaDetail.product')}</div>
                        <div className="text-sm text-gray-900 dark:text-white">{product.product_name}</div>
                      </div>
                      <div>
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('slaDetail.assignedDate')}</div>
                        <div className="text-sm text-gray-900 dark:text-white">{new Date(product.updated_at).toLocaleDateString()}</div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

                           {/* Definition Form Modal - Contract stili */}
      {showDefinitionForm && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9000] flex items-center justify-center">
            <div className="relative mx-auto w-[500px] shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
            {/* Header - Contract stili */}
            <div className="bg-[#272c39] text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-medium">
                {isEditing ? t('slaDetail.editDefinition') : t('slaDetail.addSlaDefinition')}
              </h3>
              <button 
                onClick={() => {
                setShowDefinitionForm(false);
                setIsEditing(false);
                setEditingId(null);
                }}
                className="text-white hover:text-gray-200"
              >
                <MdClose size={24} />
              </button>
            </div>
            
            <div className="p-8 bg-white dark:bg-gray-800">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('slaDetail.type')}
                </label>
                <select
                  value={formData.sla_type}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    sla_type: e.target.value,
                    custom_type: e.target.value === 'Other' ? '' : formData.custom_type 
                  })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                >
                  <option value="">{t('slaDetail.selectType')}</option>
                  {SLA_TYPES.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              {formData.sla_type === 'Other' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('slaDetail.customType')}
                  </label>
                  <input
                    type="text"
                    value={formData.custom_type}
                    onChange={(e) => setFormData({ ...formData, custom_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>
              )}

              {/* Work Hour (Start) alanı */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('slaDetail.workHourStart')}
                </label>
                <select
                  value={formData.sla_work_hours_start}
                  onChange={(e) => setFormData({ ...formData, sla_work_hours_start: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">{t('slaDetail.selectHour')}</option>
                  {HOUR_OPTIONS.map(hour => (
                    <option key={hour.value} value={hour.value}>
                      {hour.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Work Hour (End) alanı */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('slaDetail.workHourEnd')}
                </label>
                <select
                  value={formData.sla_work_hours_end}
                  onChange={(e) => setFormData({ ...formData, sla_work_hours_end: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">{t('slaDetail.selectHour')}</option>
                  {HOUR_OPTIONS.map(hour => (
                    <option key={hour.value} value={hour.value}>
                      {hour.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Work Days alanı */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('slaDetail.workDays')}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {WEEK_DAYS.map(day => (
                    <label key={day.value} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.sla_work_week.includes(day.value)}
                        onChange={() => handleWorkWeekChange(day.value)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{day.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('slaDetail.severity')}
                </label>
                <select
                  value={formData.sla_severity}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    sla_severity: e.target.value,
                    custom_severity: e.target.value === 'Other' ? '' : formData.custom_severity 
                  })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                >
                  <option value="">{t('slaDetail.selectSeverity')}</option>
                  {SEVERITY_TYPES.map(severity => (
                    <option key={severity.value} value={severity.value}>
                      {severity.label}
                    </option>
                  ))}
                </select>
              </div>

              {formData.sla_severity === 'Other' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('slaDetail.customSeverity')}
                  </label>
                  <input
                    type="text"
                    value={formData.custom_severity}
                    onChange={(e) => setFormData({ ...formData, custom_severity: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('slaDetail.responseTimeMinutes')}
                </label>
                <input
                  type="number"
                  value={formData.sla_response_time}
                  onChange={(e) => setFormData({ ...formData, sla_response_time: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('slaDetail.resolutionTimeMinutes')}
                </label>
                <input
                  type="number"
                  value={formData.sla_resoution_time}
                  onChange={(e) => setFormData({ ...formData, sla_resoution_time: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                />
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowDefinitionForm(false);
                    setIsEditing(false);
                    setEditingId(null);
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230]"
                >
                  {isEditing ? t('slaDetail.updateDefinition') : t('slaDetail.addDefinitionBtn')}
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}

      {/* Popup Component - SLA Settings stili */}
      {showPopup.show && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999] flex items-center justify-center">
          <div className="relative mx-auto w-[600px] shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
            {/* Header */}
            <div className="bg-[#272c39] text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-medium">
                {showPopup.type === 'confirm' ? 'SLA Silme' : showPopup.type === 'success' ? 'Başarılı' : 'Hata'}
              </h3>
            </div>
            
            <div className="p-8 bg-white dark:bg-gray-800">
              <div className="text-center">
                <p className="text-base text-gray-600 dark:text-gray-400 mb-6">
                  {showPopup.message}
                </p>
                <div className="flex justify-center gap-4">
                  {showPopup.type === 'confirm' && (
                    <button
                      onClick={showPopup.onCancel}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
                    >
                      İptal
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (showPopup.onConfirm) {
                        showPopup.onConfirm();
                      } else {
                        setShowPopup({ show: false });
                      }
                    }}
                    className={`px-4 py-2 text-white rounded-md ${
                      showPopup.type === 'error' 
                        ? 'bg-red-600 hover:bg-red-700'
                        : showPopup.type === 'success'
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-[#272c39] hover:bg-[#1e2230]'
                    }`}
                  >
                    {showPopup.type === 'confirm' ? 'Onayla' : 'Tamam'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

             {/* SLA Edit Modal - SLA Settings stili */}
       {showSlaEditForm && (
         <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
           <div className="relative mx-auto w-[500px] shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
            {/* Header */}
            <div className="bg-[#272c39] text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-medium">
                {t('slaDetail.edit')}
              </h3>
              <button 
                onClick={() => {
                  setShowSlaEditForm(false);
                  setIsSlaEditing(false);
                  setSlaEditingId(null);
                }}
                className="text-white hover:text-gray-200"
              >
                <MdClose size={24} />
              </button>
            </div>
            
            <div className="p-8 bg-white dark:bg-gray-800">
              <form onSubmit={handleSlaSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('slaDetail.slaGroupName')}
                  </label>
                  <input
                    type="text"
                    name="sla_group_name"
                    value={slaFormData.sla_group_name}
                    onChange={handleSlaChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('slaDetail.description')}
                  </label>
                  <textarea
                    name="description"
                    value={slaFormData.description}
                    onChange={handleSlaChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    required
                  />
                </div>

                <div className="flex justify-end gap-3 mt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowSlaEditForm(false);
                      setIsSlaEditing(false);
                      setSlaEditingId(null);
                    }}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                    type="submit"
                    className="px-4 py-2 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230]"
                  >
                    {t('slaDetail.updateDefinition')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

             {/* SLA Atama Modal - Contract stili */}
       {showAssignForm && (
         <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9998] flex items-center justify-center">
           <div className="relative mx-auto w-[600px] shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
             {/* Header - Contract stili */}
             <div className="bg-[#272c39] text-white px-6 py-4 flex justify-between items-center">
               <h3 className="text-lg font-medium">
                 {t('slaDetail.assign')}: {sla?.sla_group_name}
               </h3>
               <button 
                 onClick={() => setShowAssignForm(false)}
                 className="text-white hover:text-gray-200"
               >
                 <MdClose size={24} />
                    </button>
                  </div>
             
             <div className="p-8 bg-white dark:bg-gray-800">

            <div className="space-y-4">
              {/* Müşteri Seçimi */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('slaSettings.selectCustomer')}
                </label>
                <select
                  value={assignFormData.customer_id}
                  onChange={handleCustomerChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md"
                >
                  <option value="">{t('slaSettings.selectACustomer')}</option>
                  {customers.map(customer => (
                    <option key={customer.id} value={customer.id}>
                      {customer.customer_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Kontrat Seçimi */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('slaSettings.selectContract')}
                </label>
                <select
                  value={assignFormData.contract_id}
                  onChange={handleContractChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md"
                  disabled={!assignFormData.customer_id}
                >
                  <option value="">{t('slaSettings.selectAContract')}</option>
                  {customerContracts.map(contract => (
                    <option key={contract.id} value={contract.id}>
                      {contract.contract_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Ürün Seçimi */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('slaSettings.selectProduct')}
                </label>
                <select
                  value={assignFormData.product_id}
                  onChange={(e) => setAssignFormData(prev => ({ ...prev, product_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md"
                  disabled={!assignFormData.contract_id}
                >
                  <option value="">{t('slaSettings.selectAProduct')}</option>
                  {contractProducts.map(product => (
                    <option key={product.id} value={product.id}>
                      {product.product_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowAssignForm(false)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
                >
                  {t('common.cancel')}
                </button>
                  <button
                  onClick={handleAssignSubmit}
                  disabled={!assignFormData.product_id}
                  className="px-4 py-2 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                  {t('slaSettings.assignSla')}
                  </button>
              </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SlaDetail; 